import { useState, useEffect, useCallback } from 'react'
import {
  getReports,
  getReport,
  assignReport,
  resolveReport,
  type ReportSummary,
  type ReportDetail,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { usePermission } from '../../hooks/usePermission'

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  investigating: 'bg-blue-100 text-blue-800',
  resolved: 'bg-green-100 text-green-800',
  dismissed: 'bg-gray-100 text-gray-800',
}

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'investigating', label: 'Investigating' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'dismissed', label: 'Dismissed' },
]

export function Reports() {
  const [reports, setReports] = useState<ReportSummary[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState('')
  const [selectedReport, setSelectedReport] = useState<ReportDetail | null>(null)
  const [resolution, setResolution] = useState({ status: 'resolved', notes: '' })

  const canEdit = usePermission('reports', 'edit')

  const loadReports = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getReports({ status, page })
      if (response.ok && response.data) {
        setReports(response.data.items)
        setTotal(response.data.total)
        setTotalPages(response.data.total_pages)
      } else {
        setError(response.error?.message || 'Failed to load reports')
      }
    } catch {
      setError('Failed to connect to API')
    } finally {
      setLoading(false)
    }
  }, [status, page])

  useEffect(() => {
    loadReports()
  }, [loadReports])

  const handleOpenReport = async (report: ReportSummary) => {
    const response = await getReport(report.id)
    if (response.ok && response.data) {
      setSelectedReport(response.data)
    }
  }

  const handleAssign = async () => {
    if (!selectedReport) return
    const response = await assignReport(selectedReport.id)
    if (response.ok && response.data) {
      setSelectedReport(response.data)
      loadReports()
    }
  }

  const handleResolve = async () => {
    if (!selectedReport || !resolution.notes) return
    const response = await resolveReport(selectedReport.id, resolution.status, resolution.notes)
    if (response.ok && response.data) {
      setSelectedReport(null)
      loadReports()
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-gray-900">Reports</h2>
          <p className="text-sm text-gray-500">{total} total reports</p>
        </div>
      </div>

      <div className="mb-6">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-xl border border-gray-300 px-4 py-2 text-sm focus:border-primary focus:outline-none"
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {error && <div className="mb-6 rounded-xl bg-red-50 p-4 text-red-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : reports.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No reports found</div>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => (
            <div
              key={report.id}
              className="rounded-2xl border border-border-card bg-white p-4 cursor-pointer hover:shadow-md"
              onClick={() => handleOpenReport(report)}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-gray-900">{report.reason}</p>
                  <p className="text-sm text-gray-500">
                    {report.type} - {report.target_type} ({report.target_id.slice(0, 8)}...)
                  </p>
                </div>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[report.status]}`}>
                  {report.status}
                </span>
              </div>
              <p className="mt-2 text-xs text-gray-400">
                Reported {new Date(report.created_at).toLocaleDateString()}
              </p>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <CustomButton onClick={() => setPage((p) => p - 1)} disabled={page <= 1} variant="secondary">
            Previous
          </CustomButton>
          <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
          <CustomButton onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages} variant="secondary">
            Next
          </CustomButton>
        </div>
      )}

      {/* Report Detail Modal */}
      {selectedReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSelectedReport(null)}>
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-bold text-gray-900 mb-4">Report Details</h3>
            <div className="space-y-3 text-sm mb-6">
              <p><span className="text-gray-500">Type:</span> {selectedReport.type}</p>
              <p><span className="text-gray-500">Reason:</span> {selectedReport.reason}</p>
              {selectedReport.description && (
                <p><span className="text-gray-500">Description:</span> {selectedReport.description}</p>
              )}
              <p><span className="text-gray-500">Target:</span> {selectedReport.target_type} - {selectedReport.target_id}</p>
              <p><span className="text-gray-500">Status:</span> <span className="capitalize">{selectedReport.status}</span></p>
              {selectedReport.resolution_notes && (
                <p><span className="text-gray-500">Resolution:</span> {selectedReport.resolution_notes}</p>
              )}
            </div>

            {canEdit && selectedReport.status !== 'resolved' && selectedReport.status !== 'dismissed' && (
              <div className="space-y-4">
                {!selectedReport.assigned_to && (
                  <CustomButton onClick={handleAssign} variant="secondary" className="w-full">
                    Assign to Me
                  </CustomButton>
                )}
                <select
                  value={resolution.status}
                  onChange={(e) => setResolution((r) => ({ ...r, status: e.target.value }))}
                  className="w-full rounded-xl border border-gray-300 px-4 py-2 text-sm"
                >
                  <option value="resolved">Resolved</option>
                  <option value="dismissed">Dismissed</option>
                </select>
                <CustomTextInput
                  label="Resolution Notes"
                  value={resolution.notes}
                  onChange={(e) => setResolution((r) => ({ ...r, notes: e.target.value }))}
                  placeholder="Enter resolution notes..."
                />
                <CustomButton onClick={handleResolve} disabled={!resolution.notes} variant="primary" className="w-full">
                  Submit Resolution
                </CustomButton>
              </div>
            )}

            <CustomButton onClick={() => setSelectedReport(null)} variant="secondary" className="w-full mt-4">
              Close
            </CustomButton>
          </div>
        </div>
      )}
    </div>
  )
}
